import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Sparkles, Upload } from 'lucide-react';
import { fetchSuppliers, updateSupplierTerms } from '@/lib/api';
import { extractSupplierTerms } from '@/lib/ai-terms';
import { useNotifications } from '@/components/NotificationContext';
import type { GiftSupplier } from '@/lib/types';

export function SuppliersPage() {
  const { refresh } = useNotifications();
  const [suppliers, setSuppliers] = useState<GiftSupplier[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form-velden
  const [minOrder, setMinOrder] = useState('');
  const [transport, setTransport] = useState('');
  const [leadDays, setLeadDays] = useState('');
  const [termsText, setTermsText] = useState('');

  // AI-assist
  const [rawText, setRawText] = useState('');
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    fetchSuppliers()
      .then((s) => {
        setSuppliers(s);
        if (s.length > 0) setSelectedId(s[0].id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'))
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(
    () => suppliers.find((s) => s.id === selectedId) ?? null,
    [suppliers, selectedId]
  );

  // Velden vullen wanneer een ANDERE leverancier gekozen wordt (op id, niet op object —
  // anders reset een opslag-update de velden + de "opgeslagen"-melding direct).
  useEffect(() => {
    setSavedAt(null);
    setError(null);
    setRawText('');
    setMinOrder(selected?.min_order_value != null ? String(selected.min_order_value) : '');
    setTransport(
      selected?.transport_cost_below_min != null ? String(selected.transport_cost_below_min) : ''
    );
    setLeadDays(selected?.order_lead_time_days != null ? String(selected.order_lead_time_days) : '');
    setTermsText(selected?.terms_text ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  function numOrNull(v: string): number | null {
    const n = parseFloat(v.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await updateSupplierTerms(selected.id, {
        min_order_value: numOrNull(minOrder),
        transport_cost_below_min: numOrNull(transport),
        order_lead_time_days: numOrNull(leadDays),
        terms_text: termsText.trim() || null,
      });
      // Lokale kopie bijwerken zodat opnieuw selecteren de nieuwe waarden toont.
      setSuppliers((prev) =>
        prev.map((s) =>
          s.id === selected.id
            ? {
                ...s,
                min_order_value: numOrNull(minOrder),
                transport_cost_below_min: numOrNull(transport),
                order_lead_time_days: numOrNull(leadDays),
                terms_text: termsText.trim() || null,
              }
            : s
        )
      );
      setSavedAt(new Date().toLocaleTimeString('nl-NL'));
      refresh(); // bel bijwerken: nieuwe voorwaarden kunnen conflicten oplossen/creëren
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  }

  async function handleExtract() {
    if (!rawText.trim()) return;
    setExtracting(true);
    setError(null);
    try {
      const t = await extractSupplierTerms(rawText);
      if (t.min_order_value != null) setMinOrder(String(t.min_order_value));
      if (t.transport_cost_below_min != null) setTransport(String(t.transport_cost_below_min));
      if (t.order_lead_time_days != null) setLeadDays(String(t.order_lead_time_days));
      if (t.terms_text) setTermsText(t.terms_text);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI-extractie mislukt');
    } finally {
      setExtracting(false);
    }
  }

  async function handleFile(file: File) {
    const text = await file.text();
    setRawText(text);
  }

  if (loading) return <div className="text-sm text-bmade-500">Laden...</div>;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Leveranciers</h1>
        <p className="text-xs text-bmade-500 dark:text-bmade-400">
          Voorwaarden per leverancier. Gebruikt voor de conflict-check bij inkoop en de
          bel-notificatie.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
        {/* Leverancier-lijst */}
        <ul className="space-y-1 rounded-xl border border-bmade-200 bg-white p-2 dark:border-bmade-800 dark:bg-bmade-900/40">
          {suppliers.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setSelectedId(s.id)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  s.id === selectedId
                    ? 'bg-accent-500/10 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                    : 'text-bmade-700 hover:bg-bmade-100 dark:text-bmade-300 dark:hover:bg-bmade-800/60'
                }`}
              >
                <span className="truncate">{s.name}</span>
                {s.terms_text || s.min_order_value != null ? (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" title="Voorwaarden ingevuld" />
                ) : null}
              </button>
            </li>
          ))}
        </ul>

        {/* Form */}
        {selected && (
          <div className="space-y-4 rounded-xl border border-bmade-200 bg-white p-5 dark:border-bmade-800 dark:bg-bmade-900/40">
            <h2 className="text-lg font-semibold text-bmade-950 dark:text-white">{selected.name}</h2>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Min. orderbedrag (EUR, excl btw)">
                <input
                  type="number"
                  step={1}
                  value={minOrder}
                  onChange={(e) => setMinOrder(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Transport onder minimum (EUR)">
                <input
                  type="number"
                  step={1}
                  value={transport}
                  onChange={(e) => setTransport(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Levertijd (dagen)">
                <input
                  type="number"
                  step={1}
                  value={leadDays}
                  onChange={(e) => setLeadDays(e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="Voorwaarden (vrije tekst, komt op de inkoop-PDF)">
              <textarea
                rows={6}
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                className={`${inputCls} leading-relaxed`}
              />
            </Field>

            {/* AI-assist */}
            <details className="rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
              <summary className="cursor-pointer text-xs font-medium text-bmade-600 dark:text-bmade-400">
                Voorwaarden uit tekst halen met AI
              </summary>
              <div className="mt-2 space-y-2">
                <textarea
                  rows={4}
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Plak hier de voorwaarden uit een mail of PDF, of upload een tekstbestand..."
                  className={inputCls}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExtract}
                    disabled={extracting || !rawText.trim()}
                    className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
                  >
                    {extracting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    AI: vul velden in
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-bmade-200 px-3 py-1.5 text-xs text-bmade-700 hover:border-bmade-400 dark:border-bmade-700 dark:text-bmade-300">
                    <Upload className="h-3.5 w-3.5" />
                    Tekstbestand
                    <input
                      type="file"
                      accept=".txt,text/plain"
                      hidden
                      onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                    />
                  </label>
                  <span className="text-[10px] text-bmade-400">
                    AI vult de velden voor, jij controleert en bewaart.
                  </span>
                </div>
              </div>
            </details>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Opslaan
              </button>
              {savedAt && <span className="text-xs text-emerald-600 dark:text-emerald-400">Opgeslagen om {savedAt}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-bmade-500">{label}</span>
      {children}
    </label>
  );
}
